import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type {
	ItemFacet,
	PortalCatalogFilterFieldDto,
} from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { type ReactElement, useEffect, useState } from 'react';
import { facetParamName } from '../../lib/catalogFacetFilters';

/** Terms buckets shown before the "Show more" expander. */
const COLLAPSED_BUCKET_COUNT = 8;

interface FilterRailProps {
	/** The site's exposed filter fields, in display order (from /catalog/config). */
	fields: Array<PortalCatalogFilterFieldDto>;
	/** Facet counts/bounds from the faceted search response (keyed by field key). */
	facets: Array<ItemFacet>;
	/** Current URL params — the single source of filter state. */
	searchParams: URLSearchParams;
	/**
	 * Apply a URL mutation. The page wraps setSearchParams and resets the page
	 * param, so every control here just mutates its own params.
	 */
	onUpdate: (mutate: (next: URLSearchParams) => void) => void;
}

/**
 * The catalog's faceted filter rail: renders the storefront's configured
 * filters (per-site subset/order/labels resolved server-side) with live facet
 * counts from the current result set. Filter state lives in the URL — this
 * component only reads params and emits mutations.
 */
export function FilterRail({
	fields,
	facets,
	searchParams,
	onUpdate,
}: FilterRailProps): ReactElement | null {
	const { t } = useTranslation();
	// `| undefined` in the record types: index reads legitimately miss (a
	// field never expanded, a draft never typed) and the code branches on it.
	const [expandedFields, setExpandedFields] = useState<
		Record<string, boolean | undefined>
	>({});
	// Range/date inputs are draft-local until committed (blur / Enter) so we
	// don't rewrite the URL — and refetch — on every keystroke. Drafts reset
	// whenever the URL changes: any URL change means the previous interaction
	// completed (typing commits on blur before another control can act).
	const [drafts, setDrafts] = useState<Record<string, string | undefined>>({});

	useEffect(() => {
		setDrafts({});
	}, [searchParams]);

	if (fields.length === 0) {
		return null;
	}

	const facetByKey = new Map(facets.map((facet) => [facet.key, facet]));

	const toggleTermsValue = (
		field: PortalCatalogFilterFieldDto,
		value: string,
	): void => {
		const param = facetParamName(field);

		onUpdate((next) => {
			const current = next.getAll(param);
			next.delete(param);

			const selected = current.includes(value)
				? current.filter((entry) => entry !== value)
				: [...current, value];

			for (const entry of selected) {
				next.append(param, entry);
			}
		});
	};

	const toggleBoolean = (field: PortalCatalogFilterFieldDto): void => {
		const param = facetParamName(field);

		onUpdate((next) => {
			if (next.get(param) === 'true') {
				next.delete(param);
			} else {
				next.set(param, 'true');
			}
		});
	};

	const commitDraft = (param: string, raw: string): void => {
		const trimmed = raw.trim();

		onUpdate((next) => {
			if (trimmed.length === 0) {
				next.delete(param);
			} else {
				next.set(param, trimmed);
			}
		});
	};

	const setDateParam = (param: string, value: string): void => {
		onUpdate((next) => {
			if (value.length === 0) {
				next.delete(param);
			} else {
				next.set(param, value);
			}
		});
	};

	const renderCheckboxRow = (
		key: string,
		label: string,
		checked: boolean,
		count: number | null,
		onToggle: () => void,
	): ReactElement => (
		<label
			key={key}
			className="flex items-center gap-2 px-1 py-1 rounded cursor-pointer text-sm text-strong hover:bg-sunken"
		>
			<input
				type="checkbox"
				checked={checked}
				onChange={onToggle}
				className="h-3.5 w-3.5 shrink-0 accent-current"
			/>
			<span className="flex-1 min-w-0 truncate">{label}</span>
			{count != null && (
				<span className="text-xs text-brand-muted shrink-0">
					{t(String(count))}
				</span>
			)}
		</label>
	);

	return (
		<div className="space-y-4">
			{fields.map((field) => {
				const facet = facetByKey.get(field.key);

				// Dynamic toggles: plain on/off, no counts.
				if (field.resolution === 'dynamic') {
					const param = facetParamName(field);

					return (
						<div key={field.key}>
							{renderCheckboxRow(
								field.key,
								t(field.label),
								searchParams.get(param) === 'true',
								null,
								() => {
									toggleBoolean(field);
								},
							)}
						</div>
					);
				}

				if (field.kind === 'terms') {
					// Fixed-option custom fields fall back to their configured
					// options when the facet hasn't loaded yet; otherwise buckets
					// come from the live aggregation.
					const buckets =
						facet != null && facet.kind === 'terms'
							? facet.buckets
							: (field.options ?? []).map((option) => ({
									value: option.value,
									label: option.label,
									count: null as number | null,
								}));

					if (buckets.length === 0) {
						return null;
					}

					const param = facetParamName(field);
					const selected = new Set(searchParams.getAll(param));
					const expanded = expandedFields[field.key] === true;
					const visible = expanded
						? buckets
						: buckets.slice(0, COLLAPSED_BUCKET_COUNT);

					return (
						<div key={field.key}>
							<p className="px-1 mb-1 text-xs font-semibold uppercase tracking-wide text-brand-muted">
								{t(field.label)}
							</p>
							<div className="space-y-0.5">
								{visible.map((bucket) =>
									renderCheckboxRow(
										bucket.value,
										t(bucket.label),
										selected.has(bucket.value),
										bucket.count,
										() => {
											toggleTermsValue(field, bucket.value);
										},
									),
								)}
							</div>
							{buckets.length > COLLAPSED_BUCKET_COUNT && (
								<button
									type="button"
									onClick={() => {
										setExpandedFields((prev) => ({
											...prev,
											[field.key]: !expanded,
										}));
									}}
									className="mt-1 px-1 flex items-center gap-1 text-xs text-portal-accent"
								>
									<FontAwesomeIcon
										icon={expanded ? faChevronUp : faChevronDown}
										className="text-[10px]"
									/>
									{expanded ? t('Show fewer') : t('Show more')}
								</button>
							)}
						</div>
					);
				}

				if (field.kind === 'boolean') {
					const param = facetParamName(field);
					const trueCount =
						facet != null && facet.kind === 'boolean' ? facet.trueCount : null;

					return (
						<div key={field.key}>
							{renderCheckboxRow(
								field.key,
								t(field.label),
								searchParams.get(param) === 'true',
								trueCount,
								() => {
									toggleBoolean(field);
								},
							)}
						</div>
					);
				}

				if (field.kind === 'range') {
					const minParam = facetParamName(field, 'min');
					const maxParam = facetParamName(field, 'max');
					const bounds = facet != null && facet.kind === 'range' ? facet : null;

					// No item in the current result set has a value → dead control,
					// hide it (mirrors the empty-buckets rule for terms). EXCEPT
					// when the URL carries an active value for this field — the
					// visitor must always be able to see and clear a filter that
					// is narrowing their results. `facet == null` (counts not
					// loaded yet) keeps rendering to avoid a layout jump.
					if (
						bounds != null &&
						bounds.min == null &&
						bounds.max == null &&
						searchParams.get(minParam) == null &&
						searchParams.get(maxParam) == null
					) {
						return null;
					}

					const minPlaceholder =
						bounds?.min != null ? String(bounds.min) : t('Min');
					const maxPlaceholder =
						bounds?.max != null ? String(bounds.max) : t('Max');

					const draftValue = (param: string): string =>
						drafts[param] ?? searchParams.get(param) ?? '';

					const rangeInput = (
						param: string,
						placeholder: string,
						ariaLabel: string,
					): ReactElement => (
						<input
							type="number"
							inputMode="decimal"
							value={draftValue(param)}
							placeholder={placeholder}
							aria-label={ariaLabel}
							onChange={(e) => {
								setDrafts((prev) => ({ ...prev, [param]: e.target.value }));
							}}
							onBlur={(e) => {
								if (drafts[param] != null) {
									commitDraft(param, e.target.value);
								}
							}}
							onKeyDown={(e) => {
								if (e.key === 'Enter') {
									commitDraft(param, (e.target as HTMLInputElement).value);
								}
							}}
							className="w-full min-w-0 rounded border border-brand-border bg-brand-surface px-2 py-1 text-sm text-strong"
						/>
					);

					return (
						<div key={field.key}>
							<p className="px-1 mb-1 text-xs font-semibold uppercase tracking-wide text-brand-muted">
								{t(field.label)}
							</p>
							<div className="flex items-center gap-2 px-1">
								{rangeInput(
									minParam,
									minPlaceholder,
									`${t(field.label)} ${t('minimum')}`,
								)}
								<span className="text-xs text-brand-muted">{t('to')}</span>
								{rangeInput(
									maxParam,
									maxPlaceholder,
									`${t(field.label)} ${t('maximum')}`,
								)}
							</div>
						</div>
					);
				}

				// Only 'date' remains — the earlier returns exhaust every other kind.
				const fromParam = facetParamName(field, 'from');
				const toParam = facetParamName(field, 'to');
				const dateBounds =
					facet != null && facet.kind === 'date' ? facet : null;

				// Same data-less hiding rule as range above.
				if (
					dateBounds != null &&
					dateBounds.min == null &&
					dateBounds.max == null &&
					searchParams.get(fromParam) == null &&
					searchParams.get(toParam) == null
				) {
					return null;
				}

				const dateInput = (param: string, ariaLabel: string): ReactElement => (
					<input
						type="date"
						value={searchParams.get(param) ?? ''}
						aria-label={ariaLabel}
						onChange={(e) => {
							setDateParam(param, e.target.value);
						}}
						className="w-full min-w-0 rounded border border-brand-border bg-brand-surface px-2 py-1 text-sm text-strong"
					/>
				);

				return (
					<div key={field.key}>
						<p className="px-1 mb-1 text-xs font-semibold uppercase tracking-wide text-brand-muted">
							{t(field.label)}
						</p>
						<div className="space-y-1.5 px-1">
							{dateInput(fromParam, `${t(field.label)} ${t('from')}`)}
							{dateInput(toParam, `${t(field.label)} ${t('to')}`)}
						</div>
					</div>
				);
			})}
		</div>
	);
}
